import React from "react";
import Select, { components } from "react-select";
import FroalaEditor from "react-froala-wysiwyg";
import { DirectionalHint, Icon, TooltipDelay } from "@fluentui/react";
import CustomToolTipHost from "../../../components/CustomToolTipHost/CustomToolTipHost";
import { constantStyleForSelect } from "../../../components/CustomSelect";

const SELECT_ALL_VALUE = "__all__";

const getOptionsWithSelectAll = (options) => [
  { label: "Select All", value: SELECT_ALL_VALUE },
  ...options,
];

const CheckboxOption = (props) => {
  const isDisabled = props.selectProps.isOptionDisabled(props.data);
  const isSelectAll = props.data.value === SELECT_ALL_VALUE;

  const allOptions = props.selectProps.options.filter(
    (opt) =>
      opt.value !== SELECT_ALL_VALUE &&
      !props.selectProps.isOptionDisabled(opt)
  );

  const selectedValues = props.selectProps.value || [];

  const allSelected =
    allOptions.length > 0 &&
    allOptions.every((opt) =>
      selectedValues.find((sel) => sel.value === opt.value)
    );

  const isChecked = isSelectAll ? allSelected : props.isSelected;

  const handleOptionClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (isDisabled) return;

    if (isSelectAll) {
      props.selectProps.onChange(allSelected ? [] : allOptions, {
        action: "select-option",
        option: props.data,
      });
    } else {
      props.selectOption(props.data);
    }
  };

  return (
    <components.Option
      {...props}
      innerRef={props.innerRef}
      innerProps={{
        ...props.innerProps,
        onClick: handleOptionClick,
        style: {
          cursor: isDisabled ? "not-allowed" : "pointer",
          opacity: isDisabled ? 0.5 : 1,
          display: "flex",
          alignItems: "center",
        },
      }}
    >
      <input
        type="checkbox"
        checked={isChecked}
        disabled={isDisabled}
        onChange={() => {}}
        style={{
          marginRight: 8,
          cursor: isDisabled ? "not-allowed" : "pointer",
        }}
      />
      {props.label}
    </components.Option>
  );
};

const ValueContainer = ({ children, ...props }) => {
  const selectedValues = props.selectProps.value || [];
  const hasValue = selectedValues.length > 0;

  let summaryText = "";
  if (hasValue) {
    if (selectedValues.length <= 2) {
      summaryText = selectedValues.map((v) => v.label).join(", ");
    } else {
      summaryText = `${selectedValues
        .slice(0, 2)
        .map((v) => v.label)
        .join(", ")} +${selectedValues.length - 2} more`;
    }
  }

  return (
    <components.ValueContainer {...props}>
      <div style={{ display: "flex", alignItems: "center", width: "100%", height: "30px" }}>
        {hasValue && (
          <span
            style={{
              color: "#999",
              marginRight: "8px",
              fontSize: "14px",
            }}
          >
            {summaryText}
          </span>
        )}
        <div style={{ flex: 1 , height: "30px"}}>{children}</div>
      </div>
    </components.ValueContainer>
  );
};

const FirmTemplateSection = ({
  index,
  firmOptions,
  selectedFirms,
  onFirmChange,
  templateContent,
  onTemplateChange,
  getThemeColor,
  config,
  onRemove,
  showRemoveButton,
  disabledFirmIds = [],
}) => {
  const toolTipStyles = {
    root: {
      height: "65%",
      display: "flex",
      alignItems: "center",
    },
  };

  const filteredSelectedFirms = selectedFirms.filter(
    (item) => item.value !== SELECT_ALL_VALUE
  );

  const allFirmOptions = getOptionsWithSelectAll(firmOptions);

  return (
    <div key={index} style={{ marginBottom: "10px", position: "relative" }}>
      <div
        style={{
          position: "absolute",
          top: 28,
          right: 0,
          display: "inline-block",
        }}
      >
        <CustomToolTipHost
          delay={TooltipDelay.zero}
          id={"remove-firm-template-section"}
          directionalHint={DirectionalHint.rightCenter}
          styles={toolTipStyles}
          content={
            showRemoveButton
              ? "Remove this section"
              : "Cannot delete the last section"
          }
          className={"myToolTip"}
        >
          <Icon
            iconName="Delete"
            title="Remove Section"
            styles={{
              root: {
                fontSize: 18,
                color: showRemoveButton ? "#0078d4" : "#d0d0d0",
                cursor: showRemoveButton ? "pointer" : "not-allowed",
                opacity: showRemoveButton ? 1 : 0.5,
              },
              rootHovered: {
                color: "#b71c1c",
                opacity: 1,
              },
            }}
            onClick={showRemoveButton ? () => onRemove(index) : undefined}
          />
        </CustomToolTipHost>
      </div>

      <div className="ms-Grid" dir="ltr">
        <div className="ms-Grid-row">
          <div className="form-field ms-Grid-col ms-sm12 ms-md8 ms-lg4 ms-xl6">
            <div className="agentic-report-template-list-key">
              Client
              <span
                style={{ marginLeft: "4px", color: "red", fontWeight: "bold" }}
              >
                *
              </span>
            </div>
            <Select
              classNamePrefix="react-select"
              isMulti
              isSearchable
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              options={allFirmOptions}
              components={{
                Option: CheckboxOption,
                ValueContainer,
                MultiValue: () => null,
              }}
              value={filteredSelectedFirms}
              onChange={(value) => onFirmChange(index, value)}
              isOptionDisabled={(option) =>
                disabledFirmIds.includes(option.value)
              }
              styles={{
                ...constantStyleForSelect(getThemeColor),

                control: (base) => ({
                  ...base,
                  minHeight: "38px",
                  maxHeight: "38px",
                  width: "100%",        
                  maxWidth: "100%",     
                  minWidth: 0,  
                  display: "flex",
                  alignItems: "center", 
                }),

                valueContainer: (base) => ({
                  ...base,
                  maxHeight: "38px",
                  overflow: "hidden",
                  display: "flex",
                  alignItems: "center",
                  padding: "0 8px",
                }),

                placeholder: (base, state) => ({
                  ...base,
                  margin: 0,
                  position: "absolute",
                  top: "50%",
                  transform: "translateY(-50%)", 
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  opacity: state.isFocused || state.selectProps.menuIsOpen ? 0 : 1,
                }),

                input: (base) => ({
                  ...base,
                  margin: 0,
                  padding: 0,
                  top: "50%",
                  transform: "translateY(-50%)",
                  position: "relative",
                }),
              }}

              placeholder="Select client"
            />
          </div>
        </div>
      </div>

      <div style={{ padding: "10px", marginTop: "8px" }}>
        <div
          className="agentic-report-template-list-key"
          style={{ marginBottom: "5px" }}
        >
          Template
          <span
            style={{ marginLeft: "4px", color: "red", fontWeight: "bold" }}
          >
            *
          </span>
        </div>
        <FroalaEditor
          tag="textarea"
          model={templateContent}
          onModelChange={(value) => onTemplateChange(index, value)}
          config={config}
        />
      </div>
    </div>
  );
};

export default FirmTemplateSection;
